import React, { useEffect, useState, useRef, useCallback } from 'react';
import Api from "../../../services/api";
import moment from 'moment'; 
import {
    Box, Flex, SimpleGrid, Heading, Text, Table, Thead, Tbody,
    Tr, Th, Td, Tooltip as ChakraTooltip, useClipboard, useToast, Icon,
    TableContainer
} from '@chakra-ui/react';

import { Loading } from '../../../components/loading'; 
import StatusCard from '../../../components/UI/StatusCard';
import CustomTooltip from '../../../components/UI/CustomTooltip';
import DashboardHeader from '../../../components/Layout/DashboardHeader';

import {
    AiOutlineCalendar, AiOutlineClockCircle, AiOutlineLink, AiOutlineCheckCircle
} from 'react-icons/ai';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const CopyableCell = ({ text }) => {
    const { onCopy } = useClipboard(text || ''); 
    const toast = useToast();

    const handleCopy = () => {
        if (!text) return; 
        onCopy();
        toast({
            position: "top-right",
            duration: 2000,
            render: () => (
                <Flex
                    color="primary" p={3} bg="secondary" borderRadius="md"
                    alignItems="center" fontWeight="bold" boxShadow="lg"
                >
                    <Icon as={AiOutlineCheckCircle} w={5} h={5} mr={3} />
                    Copiado para a área de transferência!
                </Flex>
            ),
        });
    };

    return (
        <Td
            borderBottomColor="gray.700"
            onClick={handleCopy}
            cursor={text ? "pointer" : "default"} 
            _hover={text ? { bg: "gray.800" } : {}} 
        >
            <ChakraTooltip label={text || '-'} bg="gray.700" color="white" borderRadius="md" openDelay={300}>
                <Text
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxW="300px" 
                >
                    {text || '-'} 
                </Text>
            </ChakraTooltip>
        </Td>
    );
};

const PanelErpScreens = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true); 
    const [isFiltering, setIsFiltering] = useState(false); 
    const controllerRef = useRef(null);

    const defaultStartDate = moment().subtract(29, 'days').format('YYYY-MM-DD');
    const defaultEndDate = moment().format('YYYY-MM-DD');
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

     const fetchData = useCallback(async (options = {}) => {
        const { 
            triggeredByFilter = false, 
            fetchStartDate = startDate, 
            fetchEndDate = endDate     
        } = options;

        if (triggeredByFilter) {
            setIsFiltering(true); 
        } else if (!data) { 
            setLoading(true);
        }

        console.log(`[LOG - FETCH Screens] Buscando dados. Datas a serem usadas: ${fetchStartDate} a ${fetchEndDate}`);

        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        const { signal } = controllerRef.current;

        const apiStartDate = moment(fetchStartDate).format('YYYY-MM-DD'); 
        const apiEndDate = moment(fetchEndDate).format('YYYY-MM-DD');   

        const params = { startDate: apiStartDate, endDate: apiEndDate };
        console.log("[LOG - API Screens] Enviando params:", params);

        try {
            const res = await Api.get('/monitorerp/screensaccessdata', { params, signal });
            if (!signal.aborted) {
                console.log("[LOG - API Screens] Dados recebidos:", res.data);
                setData(res.data); 
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Erro ao carregar dados de acesso:', error);
                if (!signal.aborted) setData(null); 
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
                setIsFiltering(false);
            }
        }
    }, [startDate, endDate, data]); 

    const handleFilter = () => {
        console.log("[LOG - FILTER Screens] Clicado. Datas atuais:", startDate, endDate);
        if (!startDate || !endDate) { alert("Selecione datas."); return; }
        if (new Date(startDate) > new Date(endDate)) { alert("Data inicial maior que final."); return; }
        fetchData({ triggeredByFilter: true }); 
    };

    const handleClearFilter = useCallback(() => {
        console.log("[LOG - CLEAR FILTER Screens] Limpando filtro para o padrão.");
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        fetchData({ 
            triggeredByFilter: true, 
            fetchStartDate: defaultStartDate, 
            fetchEndDate: defaultEndDate 
        }); 
    }, [fetchData, defaultStartDate, defaultEndDate]);

    useEffect(() => {
        console.log("[LOG - INIT Screens] Montando componente e buscando dados iniciais.");
        fetchData({ fetchStartDate: defaultStartDate, fetchEndDate: defaultEndDate });
        
        return () => {
            console.log("[LOG - CLEANUP Screens] Desmontando componente, abortando fetch se houver.");
            controllerRef.current?.abort();
        }
    }, []); 

    if (loading && !data) return <Loading />;
    if (!loading && (!data || data.error)) return ( 
        <Flex justify="center" align="center" h="80vh" p={6}>
            <Text color="gray.400">{data?.error || 'Sem dados para exibir ou erro ao carregar.'}</Text>
        </Flex>
    );
    if (!data) return <Loading />; 
    
     const {
        period = { startDate: 'N/D', endDate: 'N/D' },
        cards = {},
        charts = { accessPerHour: [], accessPerDay: [] }, 
        grids = { baseUrlsList: [], topPages: [] } 
    } = data || {}; 

    const totalAccessMonth = cards.totalAccessMonth || 0;
    const totalAccessLastMonth = cards.totalAccessLastMonth || 0;
    const totalAccessWeek = cards.totalAccessWeek || 0;
    const totalAccessLastWeek = cards.totalAccessLastWeek || 0;
    const totalAccessToday = cards.totalAccessToday || 0;
    const totalAccessYesterday = cards.totalAccessYesterday || 0;

    const percentMonth = ((totalAccessMonth - totalAccessLastMonth) / (totalAccessLastMonth || 1)) * 100;
    const percentWeek = ((totalAccessWeek - totalAccessLastWeek) / (totalAccessLastWeek || 1)) * 100;
    const percentDay = ((totalAccessToday - totalAccessYesterday) / (totalAccessYesterday || 1)) * 100;

    const isPositiveMonth = totalAccessMonth >= totalAccessLastMonth; 
    const isPositiveWeek = totalAccessWeek >= totalAccessLastWeek;
    const isPositiveDay = totalAccessToday >= totalAccessYesterday;

    const chartHours = (charts.accessPerHour || []).map(e => ({ 
        name: `${String(e._id?.hour ?? '??').padStart(2, '0')}:00`, 
        'Acessos': e.count || 0 
    }));
    const chartDays = (charts.accessPerDay || []).map(e => ({ 
        name: `${String(e._id?.day ?? '??').padStart(2, '0')}/${String(e._id?.month ?? '??').padStart(2, '0')}`,
        'Acessos': e.count || 0 
    }));
    
    const baseUrlsData = grids.baseUrlsList || [];
    const topPagesData = grids.topPages || []; 

    const periodText = `Dados do período: ${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`;

    return (
        <Box p={6} bg="background" color="white" minH="100vh">
            <DashboardHeader
                title="Dashboard de Acesso às Telas"
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                onFilterClick={handleFilter}
                onClearFilter={handleClearFilter} 
                isDisabled={isFiltering} 
                periodText={periodText} 
            />

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
                <ChakraTooltip label={`Comparativo com período anterior (${totalAccessLastMonth.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box><StatusCard icon={AiOutlineCalendar} title="Acessos no Período" value={totalAccessMonth.toLocaleString('pt-BR')} change={`${Math.abs(percentMonth).toFixed(1)}%`} isPositive={isPositiveMonth} colorScheme="green"/></Box>
                </ChakraTooltip>
                <ChakraTooltip label={`Últimos 7d do filtro. Comp. c/ 7d ant. (${totalAccessLastWeek.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box><StatusCard icon={AiOutlineCalendar} title="Acessos (Últimos 7d)" value={totalAccessWeek.toLocaleString('pt-BR')} change={`${Math.abs(percentWeek).toFixed(1)}%`} isPositive={isPositiveWeek} colorScheme="green"/></Box>
                </ChakraTooltip>
                <ChakraTooltip label={`Último dia do filtro. Comp. c/ dia ant. (${totalAccessYesterday.toLocaleString('pt-BR')})`} bg="gray.700" color="white" borderRadius="md">
                    <Box><StatusCard icon={AiOutlineClockCircle} title="Acessos (Último Dia)" value={totalAccessToday.toLocaleString('pt-BR')} change={`${Math.abs(percentDay).toFixed(1)}%`} isPositive={isPositiveDay} colorScheme="green"/></Box>
                </ChakraTooltip>
                <ChakraTooltip label="Quantidade de Base URLs únicas acessadas no período" bg="gray.700" color="white" borderRadius="md">
                    <Box><StatusCard icon={AiOutlineLink} title="Base URLs Únicas" value={cards.baseUrls || 0} colorScheme="green" /></Box>
                </ChakraTooltip>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={8}>
                 <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Acessos nas Últimas 24 Horas</Heading>
                     {chartHours.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartHours}>
                                <defs><linearGradient id="gAccessHr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#02EA75" stopOpacity={0.4}/><stop offset="95%" stopColor="#02EA75" stopOpacity={0}/></linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis dataKey="name" stroke="#A0AEC0" tick={{ fontSize: 12 }} />
                                <YAxis stroke="#A0AEC0" tick={{ fontSize: 12 }}/>
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                <Area type="monotone" dataKey="Acessos" stroke="#02EA75" fill="url(#gAccessHr)" />
                            </AreaChart>
                        </ResponsiveContainer>
                     ) : (<Flex justify="center" align="center" h="300px"><Text color="gray.500">Sem dados por hora.</Text></Flex>)}
                </Box>
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Tendência de Acessos (Últimos 15d do Filtro)</Heading>
                     {chartDays.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartDays}>
                                <defs><linearGradient id="gAccessDay" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#02EA75" stopOpacity={0.4}/><stop offset="95%" stopColor="#02EA75" stopOpacity={0}/></linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis dataKey="name" stroke="#A0AEC0" tick={{ fontSize: 12 }} interval="preserveStartEnd"/>
                                <YAxis stroke="#A0AEC0" tick={{ fontSize: 12 }}/>
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                <Area type="monotone" dataKey="Acessos" stroke="#02EA75" fill="url(#gAccessDay)" />
                            </AreaChart>
                        </ResponsiveContainer>
                     ) : (<Flex justify="center" align="center" h="300px"><Text color="gray.500">Sem dados por dia.</Text></Flex>)}
                </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={8}>
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800" maxH="400px" overflowY="auto">
                    <Heading as="h3" size="md" color="white" mb={4}>Base URLs Mais Acessadas (Período)</Heading>
                    <TableContainer>
                        <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} zIndex="docked" bg="surface">
                                <Tr>
                                    <Th color="secondary" borderBottomColor="gray.700" >Base URL</Th>
                                    <Th color="secondary" isNumeric borderBottomColor="gray.700" w="120px">Quantidade</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {baseUrlsData.length > 0 ? baseUrlsData.map((url, index) => (
                                    <Tr key={index}>
                                        <CopyableCell text={url._id} />
                                        <Td isNumeric borderBottomColor="gray.700">{url.count}</Td>
                                    </Tr>
                                )) : (
                                     <Tr><Td colSpan={2} textAlign="center" color="gray.500">Sem dados de Base URL.</Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800" maxH="400px" overflowY="auto">
                    <Heading as="h3" size="md" color="white" mb={4}>Top 15 Telas Acessadas (Período)</Heading>
                     <TableContainer>
                        <Table variant="simple" size="sm">
                             <Thead position="sticky" top={0} zIndex="docked" bg="surface">
                                <Tr>
                                    <Th color="secondary" borderBottomColor="gray.700">Página</Th>
                                    <Th color="secondary" isNumeric borderBottomColor="gray.700" w="120px">Quantidade</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {topPagesData.length > 0 ? topPagesData.map((page, index) => ( 
                                    <Tr key={index}>
                                        <CopyableCell text={page._id} />
                                        <Td isNumeric borderBottomColor="gray.700">{page.count}</Td>
                                    </Tr>
                                )) : (
                                    <Tr><Td colSpan={2} textAlign="center" color="gray.500">Sem dados de Top Telas.</Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>
            </SimpleGrid>
        </Box>
    );
};

export default PanelErpScreens;